import React from 'react'
import { parseErrorMessage } from 'api'
import { createContext } from 'lib/createContext'
import { useLinkDayforceShiftMutation } from 'queries/admin/dayforce/useLinkDayforceShiftMutation'
import { useDayforceJobContextValue } from 'pages/Dayforce/shared/context/DayforceJobProvider'
import { Text } from '@workwhile/ui'
import { usePushWorkerAssociationMutation } from 'queries/admin/dayforce/usePushWorkerAssociationMutation'
import {
  DayforceShiftAssociationType,
  DayforceShiftType,
} from 'pages/Dayforce/constants'
import { useRemoveWorkerAssociationMutation } from 'queries/admin/dayforce/useRemoveWorkerAssociationMutation'
import { useRequestedWorkerDecisionMutation } from 'queries/admin/dayforce/useRequestedWorkerDecisionMutation'
import type { RequestedWorkerDecisionRequest } from 'api/admin/dayforce/requestedWorkerDecision'
import { useScheduleWorkItemUpdateMutation } from 'queries/admin/dayforce/useScheduleWorkItemUpdateMutation'
import { useCreateWorkerSubmissionRequestMutation } from 'queries/admin/dayforce/useCreateWorkerSubmissionRequestMutation'
import { useCreateContestedHoursRequestMutation } from 'queries/admin/dayforce/useCreateContestedHoursRequestMutation'
import { CreateContestedHoursRequest } from 'api/admin/dayforce/createContestedHoursRequest'
import { useCancelContestedHoursRequestMutation } from 'queries/admin/dayforce/useCancelContestedHoursRequestMutation'

type BookingDetailsProviderProps = {
  children: React.ReactNode
  bookingId: string
  bookingIdx: number
}

type BookingDetailsContextValue = {
  state: {
    bookingIdx: number
    dayforceBooking: NonNullable<
      ReturnType<typeof useDayforceJobContextValue>['state']['data']
    >['bookings'][number]
    linkShiftMutationState: {
      isPending: boolean
      isError: boolean
      isSuccess: boolean
      errorMessage: string | undefined
    }
    pushWorkerToDayforceMutationState: {
      isPending: boolean
      isError: boolean
      isSuccess: boolean
      errorMessage: string | undefined
    }
    removeWorkerAssociationMutationState: {
      isPending: boolean
      isError: boolean
      isSuccess: boolean
      errorMessage: string | undefined
    }
    requestedWorkerDecisionMutationState: {
      isPending: boolean
      isError: boolean
      isSuccess: boolean
      errorMessage: string | undefined
    }
    scheduleWorkItemUpdateMutationState: {
      isPending: boolean
      isError: boolean
      isSuccess: boolean
      errorMessage: string | undefined
    }
    workerSubmissionMutationState: {
      isPending: boolean
      isError: boolean
      isSuccess: boolean
      errorMessage: string | undefined
    }
    contestedHoursRequestMutationState: {
      isPending: boolean
      isError: boolean
      isSuccess: boolean
      errorMessage: string | undefined
    }
    cancelContestedHoursRequestMutationState: {
      isPending: boolean
      isError: boolean
      isSuccess: boolean
      errorMessage: string | undefined
    }
  }
  actions: {
    linkShift: (
      shiftId: number,
      associationType: DayforceShiftAssociationType
    ) => void
    pushWorkerToDayforce: (
      shiftId: number,
      workerId: number,
      shiftType: DayforceShiftType
    ) => void
    removeWorkerAssociation: (shiftId: number, associationId: number) => void
    submitRequestedWorkerDecision: (
      request: RequestedWorkerDecisionRequest
    ) => void
    scheduleWorkItemUpdate: ({
      associationId,
      shiftId,
    }: {
      associationId: number
      shiftId: number
    }) => void
    createWorkerSubmission: ({
      associationId,
      reason,
    }: {
      associationId: number
      reason: string
    }) => void
    createContestedHoursRequest: (request: CreateContestedHoursRequest) => void
    cancelContestedHoursRequest: ({
      associationId,
    }: {
      associationId: number
    }) => void
  }
}

const [Context, useBookingDetailsContextValue] =
  createContext<BookingDetailsContextValue>({
    name: 'BookingDetailsContext',
  })

export const BookingDetailsProvider = ({
  children,
  bookingId,
  bookingIdx,
}: BookingDetailsProviderProps) => {
  const {
    state: { data: dayforceJob },
  } = useDayforceJobContextValue()

  const dayforceBooking = dayforceJob?.bookings.find(
    (booking) => booking.bookingId === bookingId
  )

  const {
    mutate: linkShift,
    isPending: isLinkShiftPending,
    isError: isLinkShiftError,
    isSuccess: isLinkShiftSuccess,
    error: linkShiftError,
  } = useLinkDayforceShiftMutation()

  const {
    mutate: pushWorkerToDayforce,
    isPending: isPushWorkerToDayforcePending,
    isError: isPushWorkerToDayforceError,
    isSuccess: isPushWorkerToDayforceSuccess,
    error: pushWorkerToDayforceError,
  } = usePushWorkerAssociationMutation()

  const {
    mutate: removeWorkerAssociation,
    isPending: isRemoveWorkerAssociationPending,
    isError: isRemoveWorkerAssociationError,
    isSuccess: isRemoveWorkerAssociationSuccess,
    error: removeWorkerAssociationError,
  } = useRemoveWorkerAssociationMutation()

  const {
    mutate: submitRequestedWorkerDecisionMutate,
    isPending: isRequestedWorkerDecisionPending,
    isError: isRequestedWorkerDecisionError,
    isSuccess: isRequestedWorkerDecisionSuccess,
    error: requestedWorkerDecisionError,
  } = useRequestedWorkerDecisionMutation()

  const {
    mutate: scheduleWorkItemUpdate,
    isPending: isScheduleWorkItemUpdatePending,
    isError: isScheduleWorkItemUpdateError,
    isSuccess: isScheduleWorkItemUpdateSuccess,
    error: scheduleWorkItemUpdateError,
  } = useScheduleWorkItemUpdateMutation()

  const {
    mutate: createWorkerSubmissionRequest,
    isPending: isCreateWorkerSubmissionRequestPending,
    isError: isCreateWorkerSubmissionRequestError,
    isSuccess: isCreateWorkerSubmissionRequestSuccess,
    error: createWorkerSubmissionRequestError,
  } = useCreateWorkerSubmissionRequestMutation()

  const {
    mutate: createContestedHoursRequest,
    isPending: isCreateContestedHoursRequestPending,
    isError: isCreateContestedHoursRequestError,
    isSuccess: isCreateContestedHoursRequestSuccess,
    error: createContestedHoursRequestError,
  } = useCreateContestedHoursRequestMutation()

  const {
    mutate: cancelContestedHoursRequest,
    isPending: isCancelContestedHoursRequestPending,
    isError: isCancelContestedHoursRequestError,
    isSuccess: isCancelContestedHoursRequestSuccess,
    error: cancelContestedHoursRequestError,
  } = useCancelContestedHoursRequestMutation()

  if (!dayforceBooking) {
    return (
      <Text>
        Booking {bookingId} not found for job {dayforceJob?.jobId ?? 'unknown'}
      </Text>
    )
  }

  const value: BookingDetailsContextValue = {
    state: {
      bookingIdx,
      dayforceBooking,
      linkShiftMutationState: {
        isPending: isLinkShiftPending,
        isError: isLinkShiftError,
        isSuccess: isLinkShiftSuccess,
        errorMessage: parseErrorMessage(linkShiftError),
      },
      pushWorkerToDayforceMutationState: {
        isPending: isPushWorkerToDayforcePending,
        isError: isPushWorkerToDayforceError,
        isSuccess: isPushWorkerToDayforceSuccess,
        errorMessage: parseErrorMessage(pushWorkerToDayforceError),
      },
      removeWorkerAssociationMutationState: {
        isPending: isRemoveWorkerAssociationPending,
        isError: isRemoveWorkerAssociationError,
        isSuccess: isRemoveWorkerAssociationSuccess,
        errorMessage: parseErrorMessage(removeWorkerAssociationError),
      },
      requestedWorkerDecisionMutationState: {
        isPending: isRequestedWorkerDecisionPending,
        isError: isRequestedWorkerDecisionError,
        isSuccess: isRequestedWorkerDecisionSuccess,
        errorMessage: parseErrorMessage(requestedWorkerDecisionError),
      },
      scheduleWorkItemUpdateMutationState: {
        isPending: isScheduleWorkItemUpdatePending,
        isError: isScheduleWorkItemUpdateError,
        isSuccess: isScheduleWorkItemUpdateSuccess,
        errorMessage: parseErrorMessage(scheduleWorkItemUpdateError),
      },
      workerSubmissionMutationState: {
        isPending: isCreateWorkerSubmissionRequestPending,
        isError: isCreateWorkerSubmissionRequestError,
        isSuccess: isCreateWorkerSubmissionRequestSuccess,
        errorMessage: parseErrorMessage(createWorkerSubmissionRequestError),
      },
      contestedHoursRequestMutationState: {
        isPending: isCreateContestedHoursRequestPending,
        isError: isCreateContestedHoursRequestError,
        isSuccess: isCreateContestedHoursRequestSuccess,
        errorMessage: parseErrorMessage(createContestedHoursRequestError),
      },
      cancelContestedHoursRequestMutationState: {
        isPending: isCancelContestedHoursRequestPending,
        isError: isCancelContestedHoursRequestError,
        isSuccess: isCancelContestedHoursRequestSuccess,
        errorMessage: parseErrorMessage(cancelContestedHoursRequestError),
      },
    },
    actions: {
      linkShift: (
        shiftId: number,
        associationType: DayforceShiftAssociationType
      ) => {
        linkShift({
          agencyId: dayforceJob.agencyId,
          jobId: dayforceJob?.jobId ?? '',
          bookingId,
          shiftId: shiftId.toString(),
          associationType,
        })
      },
      pushWorkerToDayforce: (
        shiftId: number,
        workerId: number,
        shiftType: DayforceShiftType
      ) => {
        pushWorkerToDayforce({
          agencyId: dayforceJob.agencyId,
          workerId,
          shiftId,
          shiftType,
        })
      },
      removeWorkerAssociation: (shiftId: number, associationId: number) => {
        removeWorkerAssociation({
          agencyId: dayforceJob.agencyId,
          associationId,
          shiftId,
        })
      },
      submitRequestedWorkerDecision: ({
        shiftId,
        workerId,
        workerRequestId,
        reasonId,
        reasonName,
      }) => {
        submitRequestedWorkerDecisionMutate({
          agencyId: dayforceJob.agencyId,
          workerRequestId,
          workerId,
          shiftId,
          reasonId,
          reasonName,
        })
      },
      scheduleWorkItemUpdate,
      createWorkerSubmission: createWorkerSubmissionRequest,
      createContestedHoursRequest: createContestedHoursRequest,
      cancelContestedHoursRequest: cancelContestedHoursRequest,
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useBookingDetailsContextValue }
